import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshBilling } = useApp();
  const { user } = useAuth();
  const sessionId = params.get("session_id");
  const txId = params.get("transaction_id");
  const [state, setState] = useState(params.get("mm") ? "paid" : params.get("status") === "cancelled" ? "failed" : "checking");
  const tries = useRef(0);
  const home = user?.role === "therapeute" ? "/pro" : "/app";

  useEffect(() => {
    if (!sessionId) return;
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}${txId ? `?transaction_id=${txId}` : ""}`);
        if (data.payment_status === "paid") { setState("paid"); refreshBilling(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* retry */ }
      if (tries.current < 8) timer = setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, txId, refreshBilling]);

  return (
    <div className="bc-shell flex flex-col items-center justify-center px-8 text-center" data-testid="payment-success-page">
      {state === "checking" && (<><Loader2 className="animate-spin text-[#2BA3A0]" size={40} /><p className="mt-4 text-[#0A1426] font-bold">Vérification du paiement…</p></>)}
      {state === "paid" && (
        <>
          <CheckCircle2 size={64} className="text-[#2BA3A0]" />
          <h1 className="font-[Fraunces] text-3xl font-bold text-[#0A1426] mt-4">Merci !</h1>
          <p className="text-[#0A1426]/75 mt-2">Votre paiement est confirmé. Votre forfait est actif.</p>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle size={64} className="text-[#D9534F]" />
          <h1 className="font-[Fraunces] text-2xl font-bold text-[#0A1426] mt-4">{state === "failed" ? "Paiement non abouti" : "Vérification en attente"}</h1>
          <p className="text-[#0A1426]/75 mt-2">{state === "failed" ? "Aucun montant n'a été prélevé." : "Nous confirmerons votre paiement dès réception. Vous pouvez continuer."}</p>
        </>
      )}
      {state !== "checking" && (<>
        {state === "paid" && sessionId && <Button data-testid="payment-recu-btn" variant="outline" onClick={async () => { try { const { data } = await api.get(`/payments/recu/${sessionId}.pdf`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "recu-bombacare.pdf"; a.click(); } catch { /* reçu uniquement pour les offres diaspora/établissement */ } }} className="mt-4 h-14 px-8 rounded-full border-[#0A1426] text-[#0A1426] font-bold">Télécharger le reçu PDF</Button>}
        <Button data-testid="payment-continue-btn" onClick={() => navigate(home, { replace: true })} className="mt-8 h-14 px-10 rounded-full bg-[#2BA3A0] text-white font-bold">
          Continuer
        </Button>
      </>)}
    </div>
  );
}

export function PaymentCancelPage() {
  const navigate = useNavigate();
  return (
    <div className="bc-shell flex flex-col items-center justify-center px-8 text-center" data-testid="payment-cancel-page">
      <XCircle size={64} className="text-[#D4AF5A]" />
      <h1 className="font-[Fraunces] text-2xl font-bold text-[#0A1426] mt-4">Paiement annulé</h1>
      <p className="text-[#0A1426]/75 mt-2">Vous pouvez réessayer quand vous voulez.</p>
      <Button data-testid="payment-cancel-back" onClick={() => navigate(-1)} className="mt-8 h-14 px-10 rounded-full bg-[#2BA3A0] text-white font-bold">Retour</Button>
    </div>
  );
}
